(function () {
  'use strict';

  // Maps color names from variant titles to hex values (matches theme's 30-color system)
  const COLOR_HEX = {
    'yellow': '#f5e642',
    'wine red': '#7c2033', 'wine-red': '#7c2033',
    'turquoise': '#40e0d0',
    'watermelon': '#fc6c85',
    'tangerine': '#f28500',
    'silver': '#c0c0c0',
    'sage': '#87ae73',
    'rose red': '#c21e56', 'rose-red': '#c21e56',
    'purple': '#7b2d8b',
    'pink': '#ffc0cb',
    'red': '#e32636',
    'orchid': '#da70d6',
    'orange': '#ff7700',
    'navy': '#003087',
    'lilac': '#c8a2c8',
    'hot pink': '#ff69b4', 'hot-pink': '#ff69b4',
    'grey': '#808080', 'gray': '#808080',
    'dark grey': '#444444', 'dark-grey': '#444444',
    'green': '#008000',
    'dark green': '#006400', 'dark-green': '#006400',
    'cream': '#fffdd0',
    'coral': '#ff6b6b',
    'chartreuse': '#7fff00',
    'sky blue': '#87ceeb', 'sky-blue': '#87ceeb',
    'mint': '#98ff98',
    'bright blue': '#0096ff', 'bright-blue': '#0096ff',
    'denim': '#1560bd',
    'blue': '#0047ab',
    'black': '#1a1a1a',
    'white': '#f8f8f8',
  };

  function colorHexForTitle(title) {
    if (!title) return '#cccccc';
    const lower = title.toLowerCase();
    // Prefer longer matches to avoid 'red' matching 'wine red'
    const sorted = Object.keys(COLOR_HEX).sort((a, b) => b.length - a.length);
    for (const key of sorted) {
      if (lower.includes(key)) return COLOR_HEX[key];
    }
    return '#cccccc';
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  class WholesaleGallery {
    constructor() {
      this.overlay    = document.getElementById('wg-modal-overlay');
      // No modal in the DOM means the gate is showing (guest or pending) — bail.
      if (!this.overlay) return;

      this.closeBtn   = document.getElementById('wg-modal-close');
      this.modalImage = document.getElementById('wg-modal-image');
      this.modalTitle = document.getElementById('wg-modal-title');
      this.modalPrice = document.getElementById('wg-modal-price');
      this.modalDesc  = document.getElementById('wg-modal-description');
      this.variantsList = document.getElementById('wg-modal-variants');
      this.totalUnitsEl = document.getElementById('wg-total-units');
      this.totalPriceEl = document.getElementById('wg-total-price');
      this.addToCartBtn = document.getElementById('wg-add-to-cart-btn');
      this.feedback     = document.getElementById('wg-modal-feedback');

      this.currentProduct = null;
      this.quantities = {};

      this._bindEvents();
    }

    _bindEvents() {
      // Open modal on card click or Quick Shop button click
      document.querySelectorAll('.wg-card').forEach(card => {
        card.addEventListener('click', () => this._openFromCard(card));
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') this._openFromCard(card);
        });
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'button');
      });

      // Prevent quick-shop button from double-firing the card click
      document.querySelectorAll('.wg-quick-shop-btn').forEach(btn => {
        btn.addEventListener('click', e => {
          e.stopPropagation();
          this._openFromCard(btn.closest('.wg-card'));
        });
      });

      this.closeBtn.addEventListener('click', () => this._closeModal());
      this.overlay.addEventListener('click', e => {
        if (e.target === this.overlay) this._closeModal();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this._closeModal();
      });

      this.addToCartBtn.addEventListener('click', () => this._addToCart());
    }

    _openFromCard(card) {
      const scriptEl = card && card.querySelector('.wg-product-data');
      if (!scriptEl) return;
      let product;
      try {
        product = JSON.parse(scriptEl.textContent);
      } catch (err) {
        console.error('[WholesaleGallery] Failed to parse product JSON', err);
        return;
      }

      // Apply wholesale price override if the card carries one. The Liquid
      // template emits data-wholesale-amount as a decimal dollars string (e.g.
      // "8.00"), formatted by Shopify's | money_without_currency filter so it
      // respects shop currency formatting. We convert to integer cents here to
      // keep parity with how Shopify product JSON represents variant prices.
      const wholesaleAmountRaw = card.dataset.wholesaleAmount;
      if (wholesaleAmountRaw) {
        // Strip any thousands separators and currency-locale quirks, leaving
        // just the numeric portion with a decimal point.
        const normalized = wholesaleAmountRaw.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
        const dollars = parseFloat(normalized);
        if (Number.isFinite(dollars) && dollars >= 0) {
          const wholesaleCents = Math.round(dollars * 100);
          product.variants.forEach(v => { v.price = wholesaleCents; });
          product.price = wholesaleCents;
          product.price_min = wholesaleCents;
          product.price_max = wholesaleCents;
          product.price_varies = false;
          product._wholesale = true;
        }
      }

      this._openModal(product);
    }

    _openModal(product) {
      this.currentProduct = product;
      this.quantities = {};

      // Image
      const imgSrc = product.featured_image
        ? (product.featured_image.src || product.featured_image).replace(/(\.(jpe?g|png|gif|webp))(\?.*)?$/i, '_600x600$1')
        : '';
      this.modalImage.src = imgSrc;
      this.modalImage.alt = product.title;

      // Info
      this.modalTitle.textContent = product.title;
      const prices = product.variants.map(v => v.price);
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);
      this.modalPrice.textContent = minP === maxP
        ? formatMoney(minP)
        : `From ${formatMoney(minP)}`;

      // Description (plain text excerpt)
      const tmp = document.createElement('div');
      tmp.innerHTML = product.body_html || '';
      const desc = tmp.textContent.trim();
      this.modalDesc.textContent = desc.length > 220 ? desc.slice(0, 220) + '…' : desc;

      this._renderVariants(product.variants);
      this._updateSummary();

      this.overlay.classList.add('active');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus the close button for accessibility
      setTimeout(() => this.closeBtn.focus(), 50);
    }

    _closeModal() {
      this.overlay.classList.remove('active');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._clearFeedback();
    }

    _renderVariants(variants) {
      this.variantsList.innerHTML = '';

      variants.forEach(variant => {
        const row = document.createElement('div');
        row.className = 'wg-variant-row';
        if (!variant.available) row.classList.add('is-unavailable');
        row.dataset.variantId = variant.id;

        // Color swatch
        const swatch = document.createElement('div');
        swatch.className = 'wg-variant-swatch';
        swatch.style.background = colorHexForTitle(variant.title);
        if (variant.title.toLowerCase().includes('white') || variant.title.toLowerCase().includes('cream')) {
          swatch.style.border = '2px solid #ccc';
        }

        // Name + price
        const info = document.createElement('div');
        info.className = 'wg-variant-info';
        const name = document.createElement('div');
        name.className = 'wg-variant-name';
        name.textContent = variant.title === 'Default Title' ? this.currentProduct.title : variant.title;
        const price = document.createElement('div');
        price.className = 'wg-variant-price';
        price.textContent = formatMoney(variant.price) + (variant.available ? '' : ' — Sold out');
        info.appendChild(name);
        info.appendChild(price);

        // Qty stepper
        const stepper = document.createElement('div');
        stepper.className = 'wg-qty-stepper';

        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'wg-qty-btn wg-qty-dec';
        dec.textContent = '−';
        dec.setAttribute('aria-label', 'Decrease quantity');
        dec.disabled = !variant.available;

        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'wg-qty-input';
        input.min = '0';
        input.max = '9999';
        input.value = '0';
        input.setAttribute('aria-label', `Quantity for ${variant.title}`);
        input.disabled = !variant.available;

        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'wg-qty-btn wg-qty-inc';
        inc.textContent = '+';
        inc.setAttribute('aria-label', 'Increase quantity');
        inc.disabled = !variant.available;

        stepper.appendChild(dec);
        stepper.appendChild(input);
        stepper.appendChild(inc);

        row.appendChild(swatch);
        row.appendChild(info);
        row.appendChild(stepper);
        this.variantsList.appendChild(row);

        const setQty = (raw) => {
          const val = Math.max(0, Math.min(9999, parseInt(raw, 10) || 0));
          input.value = val;
          this.quantities[variant.id] = val;
          row.classList.toggle('has-qty', val > 0);
          this._updateSummary();
        };

        inc.addEventListener('click', () => setQty((parseInt(input.value, 10) || 0) + 1));
        dec.addEventListener('click', () => setQty((parseInt(input.value, 10) || 0) - 1));
        input.addEventListener('input', () => {
          this.quantities[variant.id] = parseInt(input.value, 10) || 0;
          row.classList.toggle('has-qty', this.quantities[variant.id] > 0);
          this._updateSummary();
        });
        input.addEventListener('change', () => setQty(input.value));
      });
    }

    _updateSummary() {
      if (!this.currentProduct) return;
      let units = 0;
      let cents = 0;
      this.currentProduct.variants.forEach(v => {
        const q = this.quantities[v.id] || 0;
        units += q;
        cents += q * v.price;
      });
      this.totalUnitsEl.textContent = units;
      this.totalPriceEl.textContent = formatMoney(cents);
      this.addToCartBtn.disabled = units === 0;
    }

    _clearFeedback() {
      this.feedback.textContent = '';
      this.feedback.className = 'wg-modal__feedback';
    }

    async _addToCart() {
      const items = [];
      this.currentProduct.variants.forEach(v => {
        const qty = this.quantities[v.id] || 0;
        if (qty > 0) items.push({ id: v.id, quantity: qty });
      });
      if (!items.length) return;

      const totalUnits = items.reduce((s, i) => s + i.quantity, 0);

      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = 'Adding…';
      this._clearFeedback();

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Error ${res.status}`);
        }

        this.feedback.textContent = `${totalUnits} item${totalUnits !== 1 ? 's' : ''} added to cart!`;
        this.feedback.className = 'wg-modal__feedback success';

        // Reset quantities
        this.quantities = {};
        this.variantsList.querySelectorAll('.wg-qty-input').forEach(el => { el.value = '0'; });
        this.variantsList.querySelectorAll('.wg-variant-row').forEach(el => el.classList.remove('has-qty'));
        this._updateSummary();

        // Refresh cart count bubble (standard Shopify themes)
        this._refreshCartCount();

      } catch (err) {
        this.feedback.textContent = err.message || 'Could not add to cart. Please try again.';
        this.feedback.className = 'wg-modal__feedback error';
      } finally {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = 'Add to Cart';
      }
    }

    _refreshCartCount() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('[data-cart-count], .cart-count, .cart__count, #cart-count').forEach(el => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }
  }

  document.addEventListener('DOMContentLoaded', () => new WholesaleGallery());
})();
